import { api, withIdempotencyKey, type Page } from './client'

/** Problema segnalato dal portale per una pratica sospesa/KO (`GET /api/contracts/{id}/problema-portale`, e nell'elenco). */
export interface PortalIssue {
  portale: string
  statoPortale: string | null
  motivo: string | null
  segnalatoIl: string | null
  risoltaIl: string | null
  risoltaNota: string | null
  risoltaDa: number | null
  /** Risolta nel CRM dopo l'ultima segnalazione del portale: in attesa che il reparto la ricarichi. */
  risolta: boolean
}

/**
 * Fase vera della card sul portale (Coraly) con gli ultimi messaggi della chat: il CRM traduce la fase nei suoi stati,
 * il «!» accanto allo stato dà il dettaglio. Nell'elenco e in `GET /api/contracts/{id}/coraly`.
 */
export interface PortalPhase {
  fase: string
  /** Esito definitivo sul portale (chiuso, KO, annullato, stornato): niente «!». */
  conclusiva: boolean
  aggiornataIl: string | null
  /** Ultimi due messaggi della chat della card, il più recente per primo. */
  messaggi: { autore: string | null; testo: string; il: string }[]
}

export interface Contract {
  id: number
  referenceCode: string
  status: string
  /** Solo nell'elenco, per le pratiche sospese/KO: motivo del portale per il tooltip sullo stato. */
  portalIssue?: PortalIssue | null
  /** Solo nell'elenco, per le pratiche con una card sul portale: fase vera e ultimi messaggi (il «!» accanto allo stato). */
  portalPhase?: PortalPhase | null
  /** F-099 (REQ-287-001): presenti quando la pratica e' nel cestino (`GET /api/contracts/{id}` la restituisce comunque). */
  deletedAt?: string | null
  deletedBy?: number | null
  deleteReason?: string | null
  /**
   * TASK-0025 / ADR-006: the statuses this contract could transition to right now, computed
   * server-side from the applied state-machine graph (`Contract.AvailableStatusTransitions` /
   * `ContractStatusTransitions`) and included on every contract the API returns. This is the
   * single source of truth the status-change buttons below are enabled/disabled from — the
   * frontend deliberately does NOT keep its own copy of the transition graph. See
   * `ContractDetailPage.tsx`'s comment on `renderStatusButtons` for why that was chosen over
   * duplicating the graph in TypeScript.
   */
  availableStatusTransitions: string[]
  customerId?: number
  /** Anagrafica della pratica (crm.contacts): il cliente privato o il rappresentante legale dell'azienda. */
  contactId?: number | null
  /** Codice della pratica sul portale dell'operatore (per esempio l'ID Teamwork), se già caricata. */
  operatorReferenceId?: string | null
  /** Codici portale assegnati prima dell'ultimo (ricaricamento dopo KO), dal piu' vecchio. */
  previousCodes?: string[]
  /** Nome del cliente (azienda o persona) letto dal modulo clienti; null se non collegato. */
  customerName?: string | null
  agentId?: number
  /** TASK-0026 / ADR-007: needed client-side to resolve each contract's operator
   * (Product.OperatorId) when bulk-assigning Verificata contracts to a lotto — see
   * ContractsPage.tsx's handleAssignToBatch. */
  productId?: number
  /** F-055 (TASK-0309): derived by the server from the product (never sent by the client). */
  operatorId?: number | null
  /** IMP-1: il mandante della pratica (crm.mandanti). */
  mandanteId?: number | null
  /** IMP-1: motivo "da correggere" lasciato dall'import storico. */
  dataCorrectionNote?: string | null
  departmentId?: number | null
  contractType?: string
  /**
   * REQ-006-004 (wizard "Nuova pratica" step 2: "Choose customer type - Consumer or Business").
   * A plain, independent classification field — NOT the same vocabulary as
   * `customers.ts`' `Customer.customerType` (six values: person/sole_proprietor/professional/
   * company/condominium/other). `undefined`/absent means "not yet classified".
   */
  customerSegment?: 'consumer' | 'business'
  /**
   * TASK-0204 (REQ-005-003): sales-forecast percentage (0-100) that this contract will close —
   * `Contract.Probability`. Independent of `status`/pipeline: see `assignForecast` below and
   * `Contract.SetForecast`'s own remarks for the non-coupling constraint. Always present as a key
   * on both `GET /api/contracts` (`List`) and `GET /api/contracts/{id}` (`Get`) responses, in
   * every field-visibility branch — unlike {@link baseAmount}, this is not gated by
   * `CanSeeRealAmounts` (it is a forecast, not a realised or internal economic figure).
   */
  probability?: number | null
  /**
   * TASK-0204 (REQ-005-003): the calendar date this contract is expected to close —
   * `Contract.ExpectedCloseDate`. A bare `YYYY-MM-DD` (server-side `DateOnly?`, same shape as
   * {@link paidAt}), set alongside {@link probability} through `assignForecast` below.
   */
  expectedCloseDate?: string | null
  /**
   * TASK-0162 (REQ-301-006): **optional, because the server genuinely omits it.** Both
   * `GET /api/contracts` (TASK-0159) and `GET /api/contracts/{id}` (TASK-0162) drop
   * `baseAmount`/`addonTotal`/`expectedAmount` entirely — absent keys, never `null`, never `0` —
   * for a caller whose `UserCategory` has `CanSeeRealAmounts=false`, since the company margin is
   * readable straight off them. Declaring them non-optional was a promise the API had already
   * stopped keeping on the list endpoint, and it is what would have turned TASK-0162's `Get`
   * redaction into a render crash on `ContractDetailPage.tsx` (`formatEur(undefined)`), the same
   * null-vs-undefined class of bug TASK-0161 had to fix in review. Read them through
   * {@link agentVisibleAmount} + a null-safe formatter, never bare.
   */
  baseAmount?: number
  /** TASK-0162: optional for the same reason as {@link baseAmount} — see its comment. */
  addonTotal?: number
  /** Elenco pratiche: ricavo previsto/maturato (solo per chi vede gli importi reali). */
  expectedRevenue?: number | null
  accruedRevenue?: number | null
  expectedAmount?: number
  /**
   * TASK-0159/TASK-0162: the sanitized figure served **in place of** the real amounts to a caller
   * without `CanSeeRealAmounts` — `Contract.AgentVisibleAmount`, which exists in the domain for
   * exactly this purpose. The redaction is a substitution, not a plain omission, so the two shapes
   * are mutually exclusive by construction: a response carries either the real amounts or this
   * field, never both. `null` here means "no agent-facing amount was ever recorded on this
   * practice" (the key is still present), NOT "a real amount was withheld" — which is why the type
   * admits `null` explicitly rather than only `undefined`.
   */
  agentVisibleAmount?: number | null
  /**
   * TASK-0064's closed vocabulary: `unpaid` | `partial` | `paid`. TASK-0082: writable at last, via
   * {@link contractsApi.updatePayment} — until then `Contract.RecordPayment` had no controller
   * endpoint at all and every contract created through the live app stayed at `"unpaid"` forever.
   */
  paymentStatus: string
  /** TASK-0082: amount actually collected so far. Null on every contract never touched by an incasso. */
  paidAmount?: number
  /** TASK-0082: calendar date of the incasso (`DateOnly` server-side, so a bare `YYYY-MM-DD`). */
  paidAt?: string
  /** R5-11: data reale del contratto (foglio/firma), `YYYY-MM-DD`. Se manca vale la data di caricamento. */
  signedAt?: string | null
  createdAt: string
  updatedAt?: string
}

/**
 * TASK-0082: the three payment statuses, in the order the "Incasso" controls present them. This is
 * the exact vocabulary `Contract.PaymentStatus` stores and `ContractsPage.tsx`'s "Incasso" filter
 * queries (reconciled onto these values by TASK-0064) — a fourth value written here would make those
 * rows unreachable from that filter forever, so the payment form offers these and nothing else.
 */
export const PAYMENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'unpaid', label: 'Non incassato' },
  { value: 'partial', label: 'Parziale' },
  { value: 'paid', label: 'Incassato' },
]

export interface ContractNote {
  id: number
  contractId: number
  content: string
  authorId?: number
  isInternal: boolean
  createdAt: string
}

/** TASK-0025 / ADR-006: one row of a contract's append-only status history. */
export interface ContractStatusHistoryEntry {
  id: number
  contractId: number
  fromStatus: string | null
  toStatus: string
  actorUserId?: number
  reason?: string
  note?: string
  origin: 'User' | 'Api' | 'Automation'
  occurredAt: string
}

export interface ContractCreateRequest {
  customerId?: number
  /** Agente di riferimento: lo sceglie solo lo staff, per gli agenti il server usa l'utente stesso. */
  agentId?: number
  productId?: number
  contractType?: string
  /**
   * REQ-006-005 (wizard "Nuova pratica" step 3: "Select Sector"). Server-side this already
   * threads straight into `Contract.Create`'s `departmentId` parameter
   * (`ContractsController.cs`'s `ContractCreateRequest.DepartmentId`) — this field was simply
   * never declared/sent from the frontend before this task. `undefined`/absent means "not yet
   * classified", same convention as `customerSegment` below.
   */
  departmentId?: number
  /** REQ-006-004: the wizard's Consumer/Business choice — see {@link Contract.customerSegment}. */
  customerSegment?: 'consumer' | 'business'
  baseAmount?: number
  addonTotal?: number
  notes?: string
  /** Data di firma (yyyy-MM-dd): se presente la pratica nasce già firmata. */
  signedAt?: string
}

/**
 * REQ-006-004: the two-value vocabulary `ContractCreateRequest.customerSegment` accepts, in the
 * order the wizard's radio control presents them — the exact `{ value, label }[]` shape
 * `PAYMENT_STATUS_OPTIONS` above already establishes for a closed backend vocabulary.
 */
export const CUSTOMER_SEGMENT_OPTIONS: { value: 'consumer' | 'business'; label: string }[] = [
  { value: 'consumer', label: 'Consumer' },
  { value: 'business', label: 'Business' },
]

/**
 * TASK-0048: body for `PATCH /api/contracts/{id}/amounts` (`ContractsController.UpdateAmounts`,
 * `AmountsRequest`) — `[Authorize(PolicyNames.EqualStaff)]`. This is the ONLY write path onto
 * `Contract.BaseAmount`/`AddonTotal` (`Contract.RecomputeAmounts`, "Bug #13 fix: agents cannot set
 * amounts directly") — deliberately separate from `create()` above, which must never accept amounts
 * directly. See `ContractFormPage.tsx`'s `handleSave` for the caller: a second, explicit request
 * issued only after `create()` succeeds, and only for a caller the frontend already knows is
 * EqualStaff-tier.
 */
export interface ContractAmountsRequest {
  baseAmount: number
  addonTotal: number
  agentVisibleAmount?: number
  operatorVisibleAmount?: number
}

/**
 * TASK-0025 / ADR-006: the full 19-status Italian vocabulary. Previously `ContractDetailPage.tsx`
 * hardcoded a 5-value subset (`CONTRACT_STATUSES`) that was missing `pending` entirely — a live
 * bug (a contract sitting at the single most common legacy status fell through to a generic grey
 * badge, with no button anywhere to move it forward). Centralized here so `ContractDetailPage.tsx`
 * and `ContractsPage.tsx` share exactly one label/color table instead of drifting apart again.
 */
/**
 * TASK-0082: body for `PATCH /api/contracts/{id}/payment` (`ContractsController.RecordPayment`,
 * `PaymentRequest`) — `[Authorize(PolicyNames.EqualStaff)]`, forwarded verbatim to the domain's
 * `Contract.RecordPayment`. `paidAt` is a bare `YYYY-MM-DD` calendar date (the column is `DateOnly?`,
 * not a timestamp) and is optional: an incasso whose date is not yet known is legitimate.
 */
export interface ContractPaymentRequest {
  paidAmount: number
  paymentStatus: string
  paidAt?: string
}

/**
 * TASK-0204 (REQ-005-003): body for `PATCH /api/contracts/{id}/forecast`
 * (`ContractsController.AssignForecast`, `ForecastRequest`) — `[Authorize(PolicyNames.EqualStaff)]`,
 * forwarded verbatim to the domain's `Contract.SetForecast`. `probability` is validated server-side
 * (0-100 inclusive when not null, else a 400); `expectedCloseDate` is a bare `YYYY-MM-DD` calendar
 * date (the column is `DateOnly?`, not a timestamp), same shape as {@link ContractPaymentRequest.paidAt}.
 */
export interface ContractForecastRequest {
  probability: number | null
  expectedCloseDate: string | null
}

/**
 * TASK-0274 (F-020, REQ-083-001) — "Attribuzione dei ruoli sul contratto". Body of
 * `GET /api/contracts/{id}/attribution` (`ContractAttributionController.Get`, composition root —
 * this endpoint is deliberately NOT on `ContractsController`, see that controller's own remarks for
 * why). Five roles shown together: `contractOwnerUserId`/`leadOwnerUserId` are READ-ONLY here — they
 * already exist elsewhere (`Contract.AgentId`, `Lead.AssignedAgentId`) and this endpoint only SHOWS
 * them, never writes them; the other three are the ones {@link ContractAttributionUpdateRequest}
 * writes via `PUT`. `suggested` carries prefill candidates read off the lead/appointment linked to
 * this contract (when any), for the UI to offer — never applied automatically.
 */
export interface ContractAttribution {
  contractId: number
  contractOwnerUserId: number | null
  leadOwnerUserId: number | null
  leadGeneratorUserId: number | null
  appointmentSetterUserId: number | null
  salesCloserUserId: number | null
  linkedLeadId: number | null
  linkedAppointmentId: number | null
  suggested: {
    leadGeneratorUserId: number | null
    appointmentSetterUserId: number | null
    salesCloserUserId: number | null
  }
}

/**
 * TASK-0274: body of `PUT /api/contracts/{id}/attribution`. The backend distinguishes "leave this
 * field untouched" (its JSON key absent) from "clear this field" (key present, value `null`) — see
 * `ContractAttributionController`'s own remarks for the full reasoning. This page's own "Attribuzione"
 * modal always edits and resubmits all three fields together (never a partial subset), so every call
 * from {@link contractsApi.updateAttribution} sends all three keys explicitly; a future caller that
 * genuinely needs to leave a field untouched can simply omit it, since none of the three is required.
 */
export interface ContractAttributionUpdateRequest {
  leadGeneratorUserId?: number | null
  appointmentSetterUserId?: number | null
  salesCloserUserId?: number | null
}

export const CONTRACT_STATUS_LABELS: Record<string, string> = {
  Bozza: 'Bozza',
  // 2026-10-28: bozza inviata alla firma OTP del cliente su Ermes (lo stato lo mette il job, non si sceglie a mano).
  InAttesaDiFirma: 'In attesa di firma',
  InviataAlBackoffice: 'Inviata al backoffice',
  DaVerificare: 'Da verificare',
  IntegrazioneRichiesta: 'Integrazione richiesta',
  Verificata: 'Verificata',
  InAttesaDiAssegnazioneScarico: 'In attesa di assegnazione scarico',
  AssegnataALotto: 'Assegnata a lotto',
  ApprovazioneRichiesta: 'Approvazione richiesta',
  Approvata: 'Approvata',
  CaricamentoOperatoreInCorso: 'Caricamento operatore in corso',
  BozzaOperatoreCreata: 'Bozza operatore creata',
  DaConfermare: 'Da confermare',
  InviataAllOperatore: "Inviata all'operatore",
  InLavorazioneOperatore: 'In lavorazione operatore',
  Attivata: 'Attivata',
  Rifiutata: 'Rifiutata',
  Annullata: 'Annullata',
  Sospesa: 'Sospesa',
  Stornata: 'Stornata',
}

/** Bootstrap badge color per status — same 20-value vocabulary as {@link CONTRACT_STATUS_LABELS}. */
export const CONTRACT_STATUS_COLORS: Record<string, string> = {
  Bozza: 'secondary',
  InAttesaDiFirma: 'info',
  InviataAlBackoffice: 'info',
  DaVerificare: 'warning',
  IntegrazioneRichiesta: 'warning',
  Verificata: 'primary',
  InAttesaDiAssegnazioneScarico: 'info',
  AssegnataALotto: 'info',
  ApprovazioneRichiesta: 'warning',
  Approvata: 'primary',
  CaricamentoOperatoreInCorso: 'info',
  BozzaOperatoreCreata: 'secondary',
  DaConfermare: 'warning',
  InviataAllOperatore: 'info',
  InLavorazioneOperatore: 'info',
  Attivata: 'success',
  Rifiutata: 'danger',
  Annullata: 'secondary',
  Sospesa: 'dark',
  Stornata: 'danger',
}

/**
 * TASK-0281 (F-027, REQ-035-001/002/003) — the three-colour readiness verdict, verbatim Italian names
 * exactly as `ContractReadinessColor` (backend, `JsonStringEnumConverter`) serializes them. NEVER
 * recompute this or re-derive a colour from `scorePercent` on this side: the threshold/override logic
 * (blocking-rule override, zero-denominator special case) lives entirely in
 * `ContractReadinessEvaluator` and this page only displays what the server already decided. `Giallo`
 * ("da verificare") is also what a practice the engine never actually evaluated reads as — it does
 * NOT mean "checked and pending", so no label here may imply a verification took place.
 */
export type ContractReadinessColor = 'Rosso' | 'Giallo' | 'Verde'

/** `ValidationRuleSeverity` (backend) — whether an unmet rule blocks "pronta per lo scarico" or only
 * flags the practice for a human look. */
export type ValidationRuleSeverity = 'Bloccante' | 'Avviso'

/** `ValidationRuleOutcomeStatus` (backend) — `Soddisfatta` never appears inside `unmetRules` (see
 * {@link ContractReadinessRuleOutcome}); `NonValutabile` is not a gap on this practice, it is a gap in
 * what this module can currently check — `reason` explains why, and it must be shown as such, not
 * folded into the same "missing" language as `NonSoddisfatta`. */
export type ValidationRuleOutcomeStatus = 'Soddisfatta' | 'NonSoddisfatta' | 'NonValutabile'

/**
 * TASK-0281: one entry of `ContractReadiness.unmetRules` — `ContractReadinessRuleOutcome` (backend).
 * Only `NonSoddisfatta`/`NonValutabile` rules are ever included (a satisfied rule is not "missing").
 * `reason` is always present as a key (never omitted — the backend record has no
 * `JsonIgnoreCondition`), `null` for every `NonSoddisfatta` row and a fixed explanatory sentence for
 * every `NonValutabile` one.
 */
export interface ContractReadinessRuleOutcome {
  ruleCode: string
  label: string
  severity: ValidationRuleSeverity
  status: ValidationRuleOutcomeStatus
  reason: string | null
}

/**
 * TASK-0281: body of `GET /api/contracts/{id}/readiness` (`ContractReadinessController.GetReadiness`).
 * ADR-008-gated exactly like `contractsApi.get` — 404 for both "does not exist" and "exists but not
 * visible to this caller" (never 403), so a caller who already sees the contract itself will not hit
 * that branch here.
 */
export interface ContractReadiness {
  contractId: number
  scorePercent: number
  color: ContractReadinessColor
  unmetRules: ContractReadinessRuleOutcome[]
}

/**
 * TASK-0281: body of `GET /api/contracts/readiness-summary`
 * (`ContractReadinessController.GetReadinessSummary`) — `[Authorize(PolicyNames.BackOffice)]` plus the
 * same ADR-008 row filter as every other per-contract read in this module (the aggregate is over the
 * caller's own visible book, not the whole table for a non-Admin BackOffice-tier caller). `total` is
 * the row count the aggregate was computed over — always `rosso + giallo + verde`.
 */
export interface ContractReadinessSummary {
  total: number
  rosso: number
  giallo: number
  verde: number
}

/**
 * TASK-0281: presentational label per {@link ContractReadinessColor} — verbatim the wording
 * `ContractReadinessColor.cs`'s own remarks use ("Incompleto" / "Da verificare" / "Pronta per lo
 * scarico"), so the UI never invents a fourth phrasing for what the server already named. Deliberately
 * NOT "Verificato"/"Verificata" for Giallo — see that type's own doc comment for why.
 */
export const CONTRACT_READINESS_COLOR_LABELS: Record<ContractReadinessColor, string> = {
  Rosso: 'Incompleto',
  Giallo: 'Da verificare',
  Verde: 'Pronta per lo scarico',
}

/** Bootstrap badge colour per {@link ContractReadinessColor} — same convention as
 * `CONTRACT_STATUS_COLORS` above (a bare variant name, `text-dark` applied by the caller for
 * `warning`, same as `statusBadge`'s own `textCls`). */
export const CONTRACT_READINESS_COLOR_VARIANTS: Record<ContractReadinessColor, string> = {
  Rosso: 'danger',
  Giallo: 'warning',
  Verde: 'success',
}

/** F-094 (REQ-277-001): ordinamenti accettati dal server per l'elenco delle pratiche (mai per importi). */
export type ContractSort =
  | 'created_desc'
  | 'created_asc'
  | 'signed_desc'
  | 'signed_asc'
  | 'status'
  | 'priority'
  | 'reference_asc'

export const CONTRACT_SORT_LABELS: Record<ContractSort, string> = {
  created_desc: 'Più recenti prima',
  created_asc: 'Più vecchie prima',
  signed_desc: 'Firma: più recente prima',
  signed_asc: 'Firma: più vecchia prima',
  status: 'Per stato',
  priority: 'Per priorità (urgenti prima)',
  reference_asc: 'Per riferimento',
}

export function isContractSort(value: unknown): value is ContractSort {
  return typeof value === 'string' && value in CONTRACT_SORT_LABELS
}

/** Opzione di una pratica (riga di `contract_addons` nata da un prodotto-opzione). Gli importi sono null per chi non vede gli importi. */
export interface ContractOption {
  id: number
  productId: number
  name: string
  unitPrice: number | null
  quantity: number
  totalPrice: number | null
  notes: string | null
}

/** Opzione del catalogo aggiungibile alla pratica (stesso operatore, non ancora presente). */
export interface AvailableContractOption {
  id: number
  name: string
  companyRevenue: number | null
}

/** Risposta 202 dei comandi Ermes: il job accodato (`jobId`, da seguire con `jobsApi.mine`) e il messaggio iniziale. */
export interface ErmesJobAccepted { contractId: number; message: string; jobId?: number | null }

/** Un file del cliente già nel CRM (scheda cliente o pratica precedente) che una nuova pratica riceve al salvataggio. */
export interface CustomerDocumentCandidate { id: number; name: string; documentType: string | null; origine: string }

export const contractsApi = {
  options: (id: number) => api<ContractOption[]>(`/api/contracts/${id}/options`),
  availableOptions: (id: number) => api<AvailableContractOption[]>(`/api/contracts/${id}/options/available`),
  addOption: (id: number, body: { productId: number; quantity?: number; notes?: string }) =>
    api<{ id: number }>(`/api/contracts/${id}/options`, { method: 'POST', body: JSON.stringify(body) }),
  removeOption: (id: number, optionId: number) =>
    api<void>(`/api/contracts/${id}/options/${optionId}`, { method: 'DELETE' }),
  /** Dati richiesti dal portale dell'operatore (chiavi dei campi del portale, es. ermes). Cifrati sul server. */
  getPortalData: (id: number) => api<Record<string, string>>(`/api/contracts/${id}/portal-data`),
  coraly: (id: number) => api<CoralyState>(`/api/contracts/${id}/coraly`),
  coralyLink: (id: number, processId: string, cardId: string) =>
    api<CoralyState>(`/api/contracts/${id}/coraly`, { method: 'POST', body: JSON.stringify({ processId, cardId }) }),
  coralyUnlink: (id: number) => api<void>(`/api/contracts/${id}/coraly`, { method: 'DELETE' }),
  coralySync: (id: number) => api<CoralyState>(`/api/contracts/${id}/coraly/sync`, { method: 'POST' }),
  /** «Carica sul portale»: accoda la creazione della card Coraly con i dati della pratica (staff o agente della pratica); esito con `jobsApi.mine`. */
  coralyCreate: (id: number) => api<ErmesJobAccepted>(`/api/contracts/${id}/coraly/crea`, { method: 'POST' }),
  setPortalData: (id: number, data: Record<string, string>) =>
    api<void>(`/api/contracts/${id}/portal-data`, { method: 'PUT', body: JSON.stringify(data) }),
  /** Accoda il caricamento su Teamwork (solo staff): prova o inserimento secondo la configurazione; esito nelle note. */
  uploadTeamwork: (id: number) =>
    api<{ contractId: number; apply: boolean; message: string }>(`/api/contracts/${id}/teamwork`, { method: 'POST' }),
  /** Accoda la creazione della bozza ContrattiSmart su Ermes (solo staff); numero della bozza ed esito nelle note. */
  uploadErmes: (id: number) =>
    api<ErmesJobAccepted>(`/api/contracts/${id}/ermes-draft`, { method: 'POST' }),
  /** Bozza già creata: la ricompila su Ermes con i dati corretti nel CRM (gli allegati già caricati restano). */
  /** Rimuove dalla bozza Ermes un allegato caricato dal CRM (id del portale, da ERMES_ALLEGATI nei dati portale). */
  deleteErmesAttachment: (id: number, ida: string) =>
    api<void>(`/api/contracts/${id}/ermes-draft/allegati/${encodeURIComponent(ida)}`, { method: 'DELETE' }),
  /** Copia nella pratica i documenti del suo cliente (scheda cliente e ultima pratica: carta d'identità, visura...). */
  copyCustomerDocuments: (id: number, excludeTypes: string[] = []) =>
    api<{ copiati: string[]; saltati: number; fonti?: number; dettaglio?: string }>(
      `/api/contracts/${id}/documents/from-customer${excludeTypes.length > 0 ? `?escludi=${encodeURIComponent(excludeTypes.join(','))}` : ''}`,
      { method: 'POST' }),
  /** I file del cliente (scheda cliente, pratica precedente) che una nuova pratica riceve al salvataggio. */
  customerDocuments: (customerId: number) =>
    api<{ dettaglio: string; documenti: CustomerDocumentCandidate[] }>(`/api/contracts/customer-documents/${customerId}`),
  /** Dati del cliente (anagrafica, documento, pagamento e IBAN) dalle sue pratiche precedenti, per precompilare una nuova pratica. */
  customerDefaults: (customerId: number) =>
    api<{ from: string | null; data: Record<string, string> }>(`/api/contracts/customer-defaults/${customerId}`),
  updateErmes: (id: number, allegati = false) =>
    api<ErmesJobAccepted>(`/api/contracts/${id}/ermes-draft?aggiorna=true${allegati ? '&allegati=true' : ''}`, { method: 'POST' }),
  /** Ricompila la bozza e la invia alla firma digitale OTP del cliente; gli errori del portale finiscono in ERMES_ERRORI. */
  signErmes: (id: number, prova = false) =>
    api<ErmesJobAccepted>(`/api/contracts/${id}/ermes-draft?aggiorna=true&firma=true${prova ? '&prova=true' : ''}`, { method: 'POST' }),
  /** Legge dal portale lo stato della firma (stato, link del documento) nel pannello Firma. */
  ermesSignStatus: (id: number) =>
    api<ErmesJobAccepted>(`/api/contracts/${id}/ermes-draft?aggiorna=true&stato=true`, { method: 'POST' }),
  /** Riporta la pratica allo stato BOZZA sul portale, per correggere e reinviare. */
  ermesSignCancel: (id: number) =>
    api<ErmesJobAccepted>(`/api/contracts/${id}/ermes-draft?aggiorna=true&annulla=true`, { method: 'POST' }),
  /** F-111: `departmentId`, `operatorId` e `productId` filtrano per settore, operatore e prodotto del catalogo. */
  list: (p: {
    status?: string; customerId?: number; page?: number; size?: number; sort?: ContractSort
    departmentId?: number; operatorId?: number; productId?: number
  } = {}) => {
    const q = new URLSearchParams()
    if (p.status) q.set('status', p.status)
    if (p.departmentId) q.set('departmentId', String(p.departmentId))
    if (p.operatorId) q.set('operatorId', String(p.operatorId))
    if (p.productId) q.set('productId', String(p.productId))
    if (p.sort) q.set('sort', p.sort)
    if (p.customerId) q.set('customerId', String(p.customerId))
    q.set('page', String(p.page ?? 1))
    q.set('pageSize', String(p.size ?? 25))
    return api<Page<Contract>>(`/api/contracts?${q}`)
  },

  get: (id: number) => api<Contract>(`/api/contracts/${id}`),

  create: (data: ContractCreateRequest, idempotencyKey?: string) =>
    api<{ id: number }>(
      '/api/contracts',
      withIdempotencyKey({ method: 'POST', body: JSON.stringify(data) }, idempotencyKey),
    ),

  /** TASK-0048: reuses `PATCH /{id}/amounts` exactly as it already exists — unmodified,
   * still `[Authorize(PolicyNames.EqualStaff)]`. Throws (via `api()`) on a non-2xx response,
   * including a 403 for a caller who is not actually EqualStaff-tier — callers must not swallow
   * that error. */
  updateAmounts: (id: number, data: ContractAmountsRequest) =>
    api<void>(`/api/contracts/${id}/amounts`, { method: 'PATCH', body: JSON.stringify(data) }),

  /**
   * TASK-0082: `PATCH /api/contracts/{id}/payment` (`ContractsController.RecordPayment`,
   * `PaymentRequest`) — the ONLY write path onto `paymentStatus`/`paidAmount`/`paidAt`, and the
   * first one that has ever existed: `Contract.RecordPayment` had no controller endpoint before this
   * task, so no contract created through the live app could ever leave `"unpaid"`. Carries
   * `[Authorize(PolicyNames.EqualStaff)]` plus the same row-level visibility gate as
   * `updateAmounts` (404 for a contract outside the caller's visible set). `paymentStatus` must be
   * one of {@link PAYMENT_STATUS_OPTIONS}; `paidAt` is a bare `YYYY-MM-DD` (server-side `DateOnly?`)
   * and is omitted when the date is not known.
   */
  updatePayment: (id: number, data: ContractPaymentRequest) =>
    api<void>(`/api/contracts/${id}/payment`, { method: 'PATCH', body: JSON.stringify(data) }),

  /**
   * TASK-0204 (REQ-005-003): `PATCH /api/contracts/{id}/forecast`
   * (`ContractsController.AssignForecast`, `ForecastRequest`) — the ONLY write path onto
   * `probability`/`expectedCloseDate`. Same shape as {@link updatePayment}/`updateAmounts`: carries
   * `[Authorize(PolicyNames.EqualStaff)]` plus the same row-level visibility gate (404 for a contract
   * outside the caller's visible set), and throws (via `api()`) on a non-2xx response, including the
   * 400 the backend answers for an out-of-range probability — callers must not swallow that error.
   */
  assignForecast: (id: number, data: ContractForecastRequest) =>
    api<void>(`/api/contracts/${id}/forecast`, { method: 'PATCH', body: JSON.stringify(data) }),

  /**
   * F-055 (TASK-0309, REQ-006-006): `PATCH /api/contracts/{id}/product` - chooses or changes the
   * practice's product; the server derives the operator from it. 404 outside the caller's visible set,
   * 409 once the practice is past Bozza/IntegrazioneRichiesta, 400 (Italian reason in the error
   * message) for an inactive, not-yet-valid, expired, unknown or other-department product.
   */
  assignProduct: (id: number, productId: number) =>
    api<void>(`/api/contracts/${id}/product`, { method: 'PATCH', body: JSON.stringify({ productId }) }),
  /**
   * Tipo contratto, segmento, reparto e (facoltativo) prodotto di una pratica in Bozza o Integrazione
   * richiesta, ad esempio dopo "Duplica". Tipo, segmento e reparto si sostituiscono sempre; 409 fuori
   * da quegli stati, 400 con il motivo per un prodotto non valido nel reparto scelto.
   */
  setDetails: (id: number, body: {
    contractType: string | null; customerSegment: 'consumer' | 'business' | null
    departmentId: number | null; productId?: number | null
  }) =>
    api<void>(`/api/contracts/${id}/details`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** Mandante della pratica, solo staff; null lo toglie. */
  setMandante: (id: number, mandanteId: number | null) =>
    api<void>(`/api/contracts/${id}/mandante`, { method: 'PATCH', body: JSON.stringify({ mandanteId }) }),
  /** Agente di riferimento (Contract Owner), solo staff; null lo toglie. */
  setAgent: (id: number, agentId: number | null) =>
    api<void>(`/api/contracts/${id}/agent`, { method: 'PATCH', body: JSON.stringify({ agentId }) }),
  /** Collega un cliente a una pratica già salvata (requisito per il caricamento sui portali). */
  setCustomer: (id: number, customerId: number) =>
    api<void>(`/api/contracts/${id}/customer`, { method: 'PATCH', body: JSON.stringify({ customerId }) }),
  /** Collega l'anagrafica del cliente (trovata o creata) a una pratica salvata senza contatto. */
  linkContact: (id: number) =>
    api<{ contactId: number }>(`/api/contracts/${id}/contact`, { method: 'POST' }),
  /** Data di firma (aaaa-mm-gg) o null per toglierla. */
  setSignedAt: (id: number, signedAt: string | null) =>
    api<void>(`/api/contracts/${id}/signed-at`, { method: 'PATCH', body: JSON.stringify({ signedAt }) }),
  /** Codice della pratica sul portale dell'operatore (es. numero della bozza Ermes); null per toglierlo. Solo staff. */
  setOperatorReferenceId: (id: number, operatorReferenceId: string | null) =>
    api<void>(`/api/contracts/${id}/operator-reference-id`, { method: 'PATCH', body: JSON.stringify({ operatorReferenceId }) }),

  /** TASK-0025: body is now `{ newStatus, reason, note }`, not a bare status string — the backend
   * validates the transition against the applied state machine and answers 400 on an invalid one. */
  getPortalIssue: (id: number) => api<PortalIssue | null>(`/api/contracts/${id}/problema-portale`),
  /** "Risolto, al reparto": segna corretta la pratica sospesa e avvisa il backoffice. */
  resolveSuspension: (id: number, nota: string) =>
    api<{ notificati: number; rinviata: boolean; problema: PortalIssue | null }>(`/api/contracts/${id}/sospensione-risolta`, { method: 'POST', body: JSON.stringify({ nota }) }),
  updateStatus: (id: number, newStatus: string, reason?: string, note?: string) =>
    api<void>(`/api/contracts/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ newStatus, reason, note }),
    }),

  getStatusHistory: (id: number) =>
    api<ContractStatusHistoryEntry[]>(`/api/contracts/${id}/status-history`),

  getNotes: (id: number) => api<ContractNote[]>(`/api/contracts/${id}/notes`),

  addNote: (id: number, content: string, isInternal = false) =>
    api<ContractNote>(`/api/contracts/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content, isInternal }),
    }),

  /**
   * F-099 (REQ-287-001): `DELETE /api/contracts/{id}?reason=` sposta la pratica nel cestino (soft delete, Admin).
   * Il motivo e' obbligatorio se la pratica non e' in Bozza (400). 409 `HAS_DEPENDENCIES` con
   * `dependencies: { area: conteggio }` se la pratica e' in un lotto o ha provvigioni, fatture, distinte.
   */
  delete: (id: number, reason?: string) => {
    const q = reason && reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : ''
    return api<void>(`/api/contracts/${id}${q}`, { method: 'DELETE' })
  },

  /**
   * TASK-0274 (F-020, REQ-083-001): `GET /api/contracts/{id}/attribution`
   * (`ContractAttributionController.Get`). Any authenticated caller may read it, subject to the same
   * ADR-008 row-level visibility gate as every other per-contract endpoint (404 for an invisible
   * contract, never 403).
   */
  getAttribution: (id: number) => api<ContractAttribution>(`/api/contracts/${id}/attribution`),

  /**
   * TASK-0274: `PUT /api/contracts/{id}/attribution` (`ContractAttributionController.Put`) — Admin or
   * BackOffice. Throws (via `api()`) on a non-2xx response, including the 400 the backend answers when
   * one of the ids does not resolve to a real user (naming which field/id) and the 403 for a caller
   * who is neither Admin nor BackOffice — callers must not swallow either.
   */
  updateAttribution: (id: number, data: ContractAttributionUpdateRequest) =>
    api<void>(`/api/contracts/${id}/attribution`, { method: 'PUT', body: JSON.stringify(data) }),

  /**
   * TASK-0281 (F-027, REQ-035-001): `GET /api/contracts/{id}/readiness`
   * (`ContractReadinessController.GetReadiness`). Same ADR-008 visibility gate as {@link get} — 404,
   * never 403, for an invisible contract.
   */
  getReadiness: (id: number) => api<ContractReadiness>(`/api/contracts/${id}/readiness`),

  /**
   * TASK-0281 (F-027, REQ-035-002): `GET /api/contracts/readiness-summary`
   * (`ContractReadinessController.GetReadinessSummary`) — `[Authorize(PolicyNames.BackOffice)]`, so a
   * caller below that tier gets a 403 here (see {@link ContractReadinessSummary}'s own comment).
   */
  getReadinessSummary: () => api<ContractReadinessSummary>('/api/contracts/readiness-summary'),
}

/**
 * F-018 (REQ-038-004/005): UI import esiti operatore e coda "Da riconciliare manualmente".
 * Backend: OperatorOutcomeImportsController (F-017, integrated on main).
 */

export interface OperatorOutcomeImportRow {
  id: number
  rowNumber: number
  referenceKey: string
  esito: string
  declaredOutcome: string
  data?: string
  note?: string
  matchedContractId?: number
  matchKind: string
  rowOutcome: string
  appliedAt?: string
  contractStatusHistoryId?: number
  resolvedAt?: string
}

export interface OperatorOutcomeImport {
  id: number
  createdAt: string
  createdByUserId?: number
  rowCount: number
  appliedAt?: string
  appliedByUserId?: number
  rows?: OperatorOutcomeImportRow[]
}

export interface OutcomeImportPreview {
  importId: number
  rowCount: number
  byOutcome: Record<string, number>
}

export interface OutcomeAnomalyRow {
  id: number
  importId: number
  rowNumber: number
  referenceKey: string
  esito: string
  declaredOutcome: string
  rowOutcome: string
  matchedContractId?: number
  matchKind: string
  note?: string
  createdAt: string
}

export const outcomeImportsApi = {
  /**
   * POST /api/contracts/outcome-imports (REQ-038-002) — creates the import in PREVIEW.
   * Body: raw CSV text (not JSON).
   */
  createPreview: (csvText: string) =>
    api<OutcomeImportPreview>('/api/contracts/outcome-imports', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: csvText,
    }),

  /**
   * GET /api/contracts/outcome-imports/{id} (review round) — returns the import with all rows.
   */
  getImport: (id: number) =>
    api<OperatorOutcomeImport>(`/api/contracts/outcome-imports/${id}`),

  /**
   * POST /api/contracts/outcome-imports/{id}/apply (REQ-038-002) — applies every applicable row.
   */
  applyImport: (id: number) =>
    api<{ importId: number; appliedCount: number; reRegisteredCount: number }>(
      `/api/contracts/outcome-imports/${id}/apply`,
      { method: 'POST' }
    ),

  /**
   * GET /api/contracts/outcome-imports/anomalies (REQ-038-003) — "Da riconciliare manualmente" queue.
   */
  getAnomalies: () =>
    api<OutcomeAnomalyRow[]>('/api/contracts/outcome-imports/anomalies'),

  /**
   * POST /api/contracts/outcome-imports/anomalies/{rowId}/resolve (REQ-038-003) —
   * reconciles one anomaly: links to a contract and optionally applies, or ignores with reason.
   */
  resolveAnomaly: (rowId: number, contractId?: number, reason?: string) =>
    api<{ rowId: number; action: string; applied?: boolean; rowOutcome?: string }>(
      `/api/contracts/outcome-imports/anomalies/${rowId}/resolve`,
      {
        method: 'POST',
        body: JSON.stringify({ contractId, reason }),
      }
    ),
}

/** Collegamento di una pratica alla sua card sul portale (sola lettura dei dati del portale). */
export interface CoralyEvent {
  id: number
  type: string
  occurredAt: string
  authorName: string | null
  text: string | null
  phaseFrom: string | null
  phaseTo: string | null
}

export interface CoralyState {
  linked: boolean
  /** Collegamento sospeso dal server (portale passato alla nuova piattaforma): niente «Carica sul portale» né «Aggiorna ora». */
  suspended?: boolean
  suspendedMessage?: string | null
  phaseName?: string | null
  lastSyncAt?: string | null
  /** Fase e ultimi messaggi nella stessa forma dell'elenco: il «!» accanto allo stato nella scheda. */
  portalPhase?: PortalPhase | null
  /** Solo per lo staff. */
  processId?: string | null
  cardId?: string | null
  lastError?: string | null
  events?: CoralyEvent[]
}
